import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import watermarkImage from '../assets/coverzone-watermark.png';
import { toast } from 'react-toastify';

type AccessLevel = 'none' | 'read_only' | 'read_write' | 'own_client_only';

interface RoleCol {
  key: string;
  label: string;
  isSystem: boolean;
}

interface FeatureCol {
  key: string;
  label: string;
}

interface Broker {
  id: number;
  company_name: string;
}

interface MatrixResponse {
  success: boolean;
  viewingBrokerId: number | null;
  roles: RoleCol[];
  features: FeatureCol[];
  matrix: Record<string, Record<string, AccessLevel>>;
  overridden: Record<string, string[]>;
}

const ACCESS_LABELS: Record<AccessLevel, string> = {
  none: '— None',
  read_only: 'B — Read Only',
  read_write: 'A — Read & Write',
  own_client_only: 'OCO — Own Client Only',
};

const ACCESS_COLORS: Record<AccessLevel, string> = {
  none: 'bg-gray-50 text-gray-400',
  read_only: 'bg-yellow-50 text-yellow-800',
  read_write: 'bg-green-50 text-green-800',
  own_client_only: 'bg-blue-50 text-blue-800',
};

const API_URL = process.env.REACT_APP_API_URL || '';

const PermissionsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingCell, setSavingCell] = useState<string | null>(null);

  // Super-admin broker selector
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState<number | null>(null);

  // Add-role modal
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [isCreatingRole, setIsCreatingRole] = useState(false);

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  // ---------------------------------------------------------------
  // Load the current user (from localStorage) once on mount
  // ---------------------------------------------------------------
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined') {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setIsSuperAdmin(parsed?.is_super_admin === true);
      } catch {
        setUser(null);
      }
    }
  }, []);

  // ---------------------------------------------------------------
  // Super admin: fetch the broker list for the dropdown
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    axios
      .get(`${API_URL}/api/permissions/brokers`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setBrokers(res.data?.brokers || []))
      .catch((err) => console.warn('Failed to load broker list:', err));
  }, [isSuperAdmin]);

  // ---------------------------------------------------------------
  // Fetch the matrix. Uses ?brokerId=X when the super admin has
  // selected a specific broker; otherwise shows platform defaults.
  // Broker admins ignore the dropdown (backend forces their own).
  // ---------------------------------------------------------------
  const fetchMatrix = async (brokerIdForRequest?: number | null) => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Explicit arg wins; otherwise use current selection.
      const effectiveBrokerId =
        brokerIdForRequest !== undefined ? brokerIdForRequest : selectedBrokerId;

      const url = effectiveBrokerId
        ? `${API_URL}/api/permissions/roles?brokerId=${effectiveBrokerId}`
        : `${API_URL}/api/permissions/roles`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setData(res.data);
      } else {
        setError('Unexpected response from server');
      }
    } catch (err: any) {
      console.error('Failed to load matrix:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view this page.');
      } else {
        setError(err.response?.data?.message || 'Failed to load permission matrix');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch — once we know who the user is
  useEffect(() => {
    if (user) fetchMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Re-fetch whenever the super admin changes the broker selection
  useEffect(() => {
    if (user && isSuperAdmin) {
      fetchMatrix(selectedBrokerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrokerId]);

  // ---------------------------------------------------------------
  // Cell edit
  // ---------------------------------------------------------------
  const handleChange = async (role: string, feature: string, newLevel: AccessLevel) => {
    if (!data) return;
    const previous = data.matrix[role][feature];
    if (previous === newLevel) return;

    const cellKey = `${role}/${feature}`;
    setSavingCell(cellKey);

    // Optimistic update
    setData((prev) =>
      prev
        ? {
            ...prev,
            matrix: {
              ...prev.matrix,
              [role]: { ...prev.matrix[role], [feature]: newLevel },
            },
          }
        : prev
    );

    try {
      const token = localStorage.getItem('token');
      const url = selectedBrokerId
        ? `${API_URL}/api/permissions/roles/${encodeURIComponent(role)}/features/${encodeURIComponent(
            feature
          )}?brokerId=${selectedBrokerId}`
        : `${API_URL}/api/permissions/roles/${encodeURIComponent(role)}/features/${encodeURIComponent(
            feature
          )}`;

      await axios.put(
        url,
        { access_level: newLevel },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Mark this cell as overridden if we're editing a specific broker
      if (selectedBrokerId) {
        setData((prev) => {
          if (!prev) return prev;
          const nextOverridden = { ...prev.overridden };
          const current = nextOverridden[role] || [];
          if (!current.includes(feature)) {
            nextOverridden[role] = [...current, feature];
          }
          return { ...prev, overridden: nextOverridden };
        });
      }

      toast.success('Permission updated', { autoClose: 1200 });
    } catch (err: any) {
      console.error('Failed to save permission:', err);
      // Revert
      setData((prev) =>
        prev
          ? {
              ...prev,
              matrix: {
                ...prev.matrix,
                [role]: { ...prev.matrix[role], [feature]: previous },
              },
            }
          : prev
      );
      toast.error(err.response?.data?.message || 'Failed to save permission');
    } finally {
      setSavingCell(null);
    }
  };

  // ---------------------------------------------------------------
  // Create custom role
  // ---------------------------------------------------------------
  const handleCreateRole = async () => {
    const trimmed = newRoleName.trim();
    if (!trimmed) {
      toast.error('Role name is required');
      return;
    }

    setIsCreatingRole(true);
    try {
      const token = localStorage.getItem('token');
      const url = selectedBrokerId
        ? `${API_URL}/api/permissions/roles?brokerId=${selectedBrokerId}`
        : `${API_URL}/api/permissions/roles`;

      const res = await axios.post(
        url,
        { label: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        toast.success(`Role "${trimmed}" created`);
        setShowAddRole(false);
        setNewRoleName('');
        await fetchMatrix();
      }
    } catch (err: any) {
      console.error('Failed to create role:', err);
      toast.error(err.response?.data?.message || 'Failed to create role');
    } finally {
      setIsCreatingRole(false);
    }
  };

  // ---------------------------------------------------------------
  // Delete custom role
  // ---------------------------------------------------------------
  const handleDeleteRole = async (role: RoleCol) => {
    if (role.isSystem) {
      toast.error('System roles cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete role "${role.label}"? This cannot be undone.`)) return;

    try {
      const token = localStorage.getItem('token');
      const url = selectedBrokerId
        ? `${API_URL}/api/permissions/roles/${encodeURIComponent(role.key)}?brokerId=${selectedBrokerId}`
        : `${API_URL}/api/permissions/roles/${encodeURIComponent(role.key)}`;

      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Role "${role.label}" deleted`);
      await fetchMatrix();
    } catch (err: any) {
      console.error('Failed to delete role:', err);
      toast.error(err.response?.data?.message || 'Failed to delete role');
    }
  };

  const filteredFeatures = useMemo(() => {
    if (!data) return [];
    if (!searchTerm.trim()) return data.features;
    const term = searchTerm.toLowerCase();
    return data.features.filter(
      (f) => f.label.toLowerCase().includes(term) || f.key.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  // Live slug preview for the create modal
  const slugPreview = newRoleName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  const isOverridden = (roleKey: string, featureKey: string): boolean =>
    !!data?.overridden?.[roleKey]?.includes(featureKey);

  if (!user) return <div className="p-8">Loading user...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5 z-0 select-none"
        />
      </div>

      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Permissions"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        <div className="flex-1 p-6">
          <div className="max-w-[1800px] mx-auto">
            <div className="mb-6 flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Role Permission Matrix</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {isSuperAdmin && !selectedBrokerId
                    ? 'Platform defaults apply to every broker that has not overridden them.'
                    : 'Configure what each role can do. Changes take effect immediately for all users with that role.'}
                </p>
              </div>
              <button
                onClick={() => setShowAddRole(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Role
              </button>
            </div>

            {/* Super admin: broker selector */}
            {isSuperAdmin && (
              <div className="mb-4 flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <label className="text-sm font-medium text-indigo-900">Viewing:</label>
                <select
                  value={selectedBrokerId ?? ''}
                  onChange={(e) =>
                    setSelectedBrokerId(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="px-3 py-2 border border-indigo-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Platform Defaults (all brokers)</option>
                  {brokers.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.company_name}
                    </option>
                  ))}
                </select>
                {selectedBrokerId && (
                  <span className="text-xs text-indigo-700">
                    Cells marked with a dot have been overridden by this broker.
                  </span>
                )}
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-sm flex items-start gap-2">
              <svg
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                <strong>A</strong> = Read &amp; Write ·{' '}
                <strong>B</strong> = Read Only ·{' '}
                <strong>OCO</strong> = Own Client Only ·{' '}
                <strong>—</strong> = No access
              </span>
            </div>

            <div className="mb-4 flex items-center gap-3">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter features..."
                className="w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
              <div className="text-sm text-gray-500">
                {filteredFeatures.length} of {data?.features.length ?? 0} features
              </div>
              <div className="flex-1" />
              <button
                onClick={() => fetchMatrix()}
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {isLoading && !data ? (
              <div className="text-center py-12 text-gray-500">Loading permission matrix...</div>
            ) : !data ? (
              <div className="text-center py-12 text-gray-500">No data available.</div>
            ) : (
              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 z-20 min-w-[220px] border-r border-gray-200">
                          Role
                        </th>
                        {filteredFeatures.map((f) => (
                          <th
                            key={f.key}
                            className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap min-w-[130px]"
                          >
                            {f.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.roles.map((role) => (
                        <tr key={role.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-white border-r border-gray-200 z-10">
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{role.label}</div>
                                {!role.isSystem && (
                                  <div className="text-[10px] uppercase text-gray-400 tracking-wide">
                                    Custom
                                  </div>
                                )}
                              </div>
                              {!role.isSystem && (
                                <button
                                  onClick={() => handleDeleteRole(role)}
                                  title="Delete custom role"
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          {filteredFeatures.map((feature) => {
                            const level = data.matrix[role.key]?.[feature.key] ?? 'none';
                            const cellKey = `${role.key}/${feature.key}`;
                            const isSaving = savingCell === cellKey;
                            const overriddenCell = isOverridden(role.key, feature.key);

                            return (
                              <td
                                key={feature.key}
                                className="px-3 py-2 whitespace-nowrap relative"
                              >
                                <select
                                  value={level}
                                  onChange={(e) =>
                                    handleChange(
                                      role.key,
                                      feature.key,
                                      e.target.value as AccessLevel
                                    )
                                  }
                                  disabled={isSaving || (feature.key === 'manage_permissions' && !isSuperAdmin)}
                                  className={`w-full px-2 py-1 text-xs font-medium rounded border-none focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer ${
                                    ACCESS_COLORS[level]
                                  } ${isSaving ? 'opacity-50' : ''}`}
                                >
                                  <option value="none">{ACCESS_LABELS.none}</option>
                                  <option value="read_only">{ACCESS_LABELS.read_only}</option>
                                  <option value="read_write">{ACCESS_LABELS.read_write}</option>
                                  <option value="own_client_only">
                                    {ACCESS_LABELS.own_client_only}
                                  </option>
                                </select>
                                {overriddenCell && (
                                  <span
                                    title="This cell is overridden from the platform default"
                                    className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-indigo-500 pointer-events-none"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Role Modal */}
      {showAddRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Custom Role</h3>
              <button
                onClick={() => {
                  setShowAddRole(false);
                  setNewRoleName('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name *
                </label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Senior Claims Adjuster"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateRole();
                    if (e.key === 'Escape') {
                      setShowAddRole(false);
                      setNewRoleName('');
                    }
                  }}
                />
                {newRoleName.trim() && (
                  <p className="text-xs text-gray-500 mt-1">
                    Key:{' '}
                    <code className="bg-gray-100 px-1 rounded">
                      {slugPreview || '(invalid)'}
                    </code>
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {selectedBrokerId
                  ? "This role will only be visible to users of the selected broker."
                  : isSuperAdmin
                  ? 'Select a broker above to create a broker-scoped role. Without a selection, super admin cannot create roles.'
                  : 'The new role will appear in the matrix with all permissions set to None. Configure its access by editing the cells after saving.'}
              </p>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAddRole(false);
                  setNewRoleName('');
                }}
                disabled={isCreatingRole}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                disabled={
                  isCreatingRole || !newRoleName.trim() || (isSuperAdmin && !selectedBrokerId)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingRole ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsAdmin;